import React, { useState, useEffect, useMemo } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus } from 'lucide-react';

interface CalEvent { id: string; date: string; title: string; }

const EVENTS_KEY = 'xf_calendar_events';

function loadEvents(): CalEvent[] {
  try { return JSON.parse(localStorage.getItem(EVENTS_KEY) || '[]'); } catch { return []; }
}
function saveEvents(e: CalEvent[]) { try { localStorage.setItem(EVENTS_KEY, JSON.stringify(e)); } catch { /* quota exceeded */ } }

export function CalendarPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [events, setEvents] = useState<CalEvent[]>(loadEvents);
  const [selected, setSelected] = useState<string>('');
  const [newTitle, setNewTitle] = useState('');

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const cells = useMemo(() => {
    const arr: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    return arr;
  }, [firstDay, daysInMonth]);

  const eventsOf = (d: number) =>
    events.filter(e => e.date === `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);

  const addEvent = () => {
    if (!selected || !newTitle.trim()) return;
    const next = [...events, { id: Date.now().toString(), date: selected, title: newTitle.trim() }];
    setEvents(next); saveEvents(next); setNewTitle('');
  };

  const prev = () => { const m = month - 1; if (m < 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m); };
  const nextM = () => { const m = month + 1; if (m > 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m); };

  return (
    <div className="flex flex-col h-full text-white/80">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="text-celestial-saturn" size={20} />
          <h2 className="text-lg font-black">{year}年 {month + 1}月</h2>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prev} className="p-2 rounded-xl hover:bg-white/10 text-white/50"><ChevronLeft size={18} /></button>
          <button onClick={nextM} className="p-2 rounded-xl hover:bg-white/10 text-white/50"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-white/40 mb-1">
        {['日', '一', '二', '三', '四', '五', '六'].map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1 flex-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const ds = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const evs = eventsOf(d);
          const isToday = ds === todayStr;
          const isSel = ds === selected;
          return (
            <button key={i} onClick={() => setSelected(ds)}
              className={`relative flex flex-col items-center justify-start pt-1.5 rounded-xl text-sm transition-colors
                ${isToday ? 'bg-celestial-saturn/20 text-celestial-saturn font-bold' : 'text-white/70 hover:bg-white/5'}
                ${isSel ? 'ring-1 ring-celestial-saturn/50' : ''}`}>
              {d}
              {evs.length > 0 && <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-celestial-saturn" />}
            </button>
          );
        })}
      </div>
      <div className="mt-3 p-3 rounded-2xl bg-white/[0.03] border border-white/10">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-bold text-white/60">{selected || '选择日期添加事件'}</span>
        </div>
        <div className="flex gap-2">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="事件标题"
            disabled={!selected}
            className="flex-1 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white/80 placeholder:text-white/30 focus:outline-none focus:border-celestial-saturn/40 disabled:opacity-40"
          />
          <button onClick={addEvent} disabled={!selected || !newTitle.trim()}
            className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-celestial-saturn/20 border border-celestial-saturn/30 text-celestial-saturn text-xs font-bold hover:bg-celestial-saturn/30 transition-all disabled:opacity-30">
            <Plus size={12} />添加
          </button>
        </div>
        <div className="space-y-1 max-h-24 overflow-y-auto custom-scrollbar mt-2">
          {/* Bug 21: 月份切换后直接用 selected 字符串过滤，避免使用 eventsOf 导致日期错误 */}
          {selected && events.filter(e => e.date === selected).map(e => (
            <div key={e.id} className="text-xs text-white/70 flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-celestial-saturn" />{e.title}</div>
          ))}
          {selected && events.filter(e => e.date === selected).length === 0 && <div className="text-[11px] text-white/30">暂无事件</div>}
        </div>
      </div>
    </div>
  );
}
